import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getIn from 'immutable-less/transmute/getIn';
import { useQuery } from 'data-fetching-client';
import { GET_OBJECT_TYPE_BY_ID } from 'automation-ui-references/object-type-properties/ObjectTypesQueries';
import { COMPANY_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import OwnerName from 'automation-ui-references/customer-data/OwnerName';
import PropertyName from 'automation-ui-references/object-type-properties/PropertyName';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import { getServiceTokenPrefix } from 'automation-ui-action-features/personalized-text/personalizedTextUtils';
import * as PropertyValueTypes from 'automation-ui-flow-core/fields/PropertyValueTypes';
import * as InternalPropertyDefaults from 'automation-ui-action-features/object-type-properties/InternalPropertyDefaults';
import CreateObjectTitleLabel from 'automation-ui-action-features/actions/create-object/CreateObjectTitleLabel';
import PersonalizedTextView from 'automation-ui-action-features/personalized-text/PersonalizedTextView';
import { jsx as _jsx } from "react/jsx-runtime";
const CreateObjectActionDetail = ({
  actionMetadata,
  flowObjectTypeId,
  messageLangPath = 'actionDetail'
}) => {
  const {
    objectTypeId,
    properties
  } = actionMetadata;
  const {
    data
  } = useQuery(GET_OBJECT_TYPE_BY_ID, {
    skip: !objectTypeId,
    // @ts-expect-error FIXME: update code for `TypedDocumentNode` changes
    variables: {
      objectTypeId
    }
  });
  if (!flowObjectTypeId) {
    return null;
  }
  const objectTypeDefinition = getIn(['objectTypeById'], data);
  const primaryDisplayLabel = objectTypeDefinition && objectTypeDefinition.getPrimaryDisplayLabelProperty();
  const secondaryDisplayLabels = objectTypeDefinition && objectTypeDefinition.getSecondaryDisplayLabels();
  let propertyAssignment = properties.find(property => property.targetProperty === primaryDisplayLabel);
  // company objects have two possible primary display labels
  // only one is listed
  if (!propertyAssignment && objectTypeId === COMPANY_ID) {
    propertyAssignment = properties.find(property => property.targetProperty === 'domain');
  }

  // If no primary display label has been found, consider secondary display labels
  if (!propertyAssignment && secondaryDisplayLabels) {
    propertyAssignment = properties.find(property => secondaryDisplayLabels.includes(property.targetProperty));
  }

  // If no display label is found or its an unsupported multi, don't render it.
  if (!propertyAssignment || getIn(['value', 'type'], propertyAssignment) === PropertyValueTypes.MULTI_VALUE) {
    return /*#__PURE__*/_jsx("div", {
      children: messageLangPath === 'actionDetail' ? /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
        baseLangKey: "automation-ui-action-features.action-features.create-object.action-detail.no-label_cob",
        objectTypeId: objectTypeId
      }) : /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
        baseLangKey: "automation-ui-action-features.action-features.create-object.action-preview.no-label_cob",
        objectTypeId: objectTypeId
      })
    });
  }
  const getOwnerLabel = () => {
    const ownerPropertyAssignment = properties.find(property => property.targetProperty === InternalPropertyDefaults.HUBSPOT_OWNER);
    if (!ownerPropertyAssignment || getIn(['value', 'type'], ownerPropertyAssignment) === PropertyValueTypes.STATIC_VALUE && !getIn(['value', 'value'], ownerPropertyAssignment)) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-action-features.action-features.create-object.noOwnerAssignment"
      });
    }
    if (getIn(['value', 'type'], ownerPropertyAssignment) === PropertyValueTypes.STATIC_VALUE) {
      // STATIC_VALUE types are NEVER arrays
      const value = getIn(['value', 'value'], ownerPropertyAssignment) || '';
      return /*#__PURE__*/_jsx(OwnerName, {
        id: Array.isArray(value) ? '' : value,
        href: null
      });
    } else {
      return /*#__PURE__*/_jsx(PropertyName, {
        objectTypeId: flowObjectTypeId,
        propertyName: getIn(['value', 'propertyName'], ownerPropertyAssignment) || ''
      });
    }
  };
  const isStaticPropertyType = getIn(['value', 'type'], propertyAssignment) === PropertyValueTypes.STATIC_VALUE;
  if (isStaticPropertyType) {
    const options = {
      owner: /*#__PURE__*/_jsx(Text, {
        as: "strong",
        typography: "heading-50",
        children: getOwnerLabel()
      }),
      title: /*#__PURE__*/_jsx(Text, {
        as: "strong",
        typography: "heading-50",
        children: /*#__PURE__*/_jsx(CreateObjectTitleLabel, {
          flowObjectTypeId: flowObjectTypeId,
          propertyAssignment: propertyAssignment.value
        })
      })
    };
    return /*#__PURE__*/_jsx("div", {
      children: messageLangPath === 'actionDetail' ? /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
        baseLangKey: "automation-ui-action-features.action-features.create-object.action-detail.static_cob",
        objectTypeId: objectTypeId,
        options: options
      }) : /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
        baseLangKey: "automation-ui-action-features.action-features.create-object.action-preview.static_cob",
        objectTypeId: objectTypeId,
        options: options
      })
    });
  }
  const targetProperty = getIn(['targetProperty'], propertyAssignment);
  const options = {
    copyFromProperty: /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(CreateObjectTitleLabel, {
        flowObjectTypeId: flowObjectTypeId,
        propertyAssignment: propertyAssignment.value
      })
    }),
    copyToProperty: /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(PersonalizedTextView, {
        propertyTypeId: objectTypeId,
        text: `{{ ${getServiceTokenPrefix(objectTypeId)}.${targetProperty} }}`
      })
    }),
    owner: /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-50",
      children: getOwnerLabel()
    })
  };
  return /*#__PURE__*/_jsx("div", {
    children: messageLangPath === 'actionDetail' ? /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
      baseLangKey: "automation-ui-action-features.action-features.create-object.action-detail.dynamic_cob",
      objectTypeId: objectTypeId,
      options: options
    }) : /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
      baseLangKey: "automation-ui-action-features.action-features.create-object.action-preview.dynamic_cob",
      objectTypeId: objectTypeId,
      options: options
    })
  });
};
export default CreateObjectActionDetail;